import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BASE_URL = "http://nissi-india.com"; // change if needed

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Similar to C# MidpointRounding.AwayFromZero (good for positive amounts)
function roundAwayFromZero(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return n >= 0 ? Math.floor(n + 0.5) : Math.ceil(n - 0.5);
}

function sumBy(rows, key) {
  return rows.reduce((acc, r) => acc + toNum(r?.[key]), 0);
}

const EmployeePFListPrint = () => {
  const q = useQuery();

  const finYearId = q.get("finYearId") || localStorage.getItem("finYearId") || "";
  const branchId = q.get("branchId") || "Select";
  const monthName = q.get("monthName") || "Select";
  const year = q.get("year") || "Select";
  const departmentId = q.get("departmentId") || "Select";
  const empCode = q.get("empCode") || "";
  const empName = q.get("empName") || "";
  const paymode = q.get("paymode") || "Select";

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Optional company info (use localStorage; you can later wire an API)
  const companyName = localStorage.getItem("companyName") || "Nissi Infotech";
  const companyLine1 = localStorage.getItem("companyLine1") || "Nissi";
  const companyLine2 = localStorage.getItem("companyLine2") || "CIVIL CONSTRUCTION";
  const companyAddr =
    localStorage.getItem("companyAddr") ||
    "3/42-12, Nagappa colony, kavundampalayam - 641030. Tamil Nadu.";
  const companyPhone = localStorage.getItem("companyPhone") || "Phone No : 9003744644";

  const fetchPrintData = async () => {
    if (!String(finYearId).trim()) return;

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/employee-pf-list/print-data`, {
        params: {
          finYearId: Number(finYearId),
          branchId,
          monthName,
          year,
          departmentId,
          empCode,
          empName,
          paymode,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrintData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Totals (based on your dataset keys from usp_employeepflist)
  const totals = useMemo(() => {
    const tBasic = sumBy(rows, "wages");
    const tLeave = sumBy(rows, "fld_absence");
    const tLop = sumBy(rows, "fld_lop");
    const tPaidBasic = sumBy(rows, "fld_paidbasic");
    const tDiff = sumBy(rows, "otherallowance");
    const tPaidGross = sumBy(rows, "totalwages");
    const tPf12 = sumBy(rows, "fld_pf");
    const tPf833 = sumBy(rows, "fld_pf8");
    const tPf367 = sumBy(rows, "fld_pf3");

    const chNo2 = (tPf12 / 12) * 0.5;
    const chNo21 = (tPf12 / 12) * 0.5;
    const totalPfAll = tPf12 + tPf833 + tPf367 + chNo2 + chNo21;

    return {
      tBasic,
      tLeave,
      tLop,
      tPaidBasic,
      tDiff,
      tPaidGross,
      tPf12,
      tPf833,
      tPf367,
      chNo2,
      chNo21,
      totalPfAll,
    };
  }, [rows]);

  const handlePrint = () => window.print();

  return (
    <div className="print-root">
      <style>{`
        /* Screen */
        .print-root{
          background:#fff;
          color:#000;
          padding: 16px;
          font-family: "Times New Roman", Times, serif;
        }
        .page {
          border: 2px solid #000;
          padding: 14px;
          max-width: 1100px;
          margin: 0 auto;
        }
        .headerBox{
          border:1px solid #000;
          padding: 10px;
          display:flex;
          gap:14px;
          align-items:center;
          justify-content:space-between;
        }
        .logoBox{
          width:120px;
          height:70px;
          border:1px solid #000;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight:700;
          font-size:12px;
        }
        .companyText{
          flex:1;
          text-align:center;
          line-height:1.15;
        }
        .companyText .name{ font-weight:700; font-size:20px; }
        .companyText .sub{ font-weight:700; font-size:13px; }
        .companyText .addr{ font-size:12px; margin-top:2px; }
        .title{
          margin-top: 10px;
          text-align:center;
          font-weight:700;
          font-size:22px;
        }
        table{
          width:100%;
          border-collapse:collapse;
          margin-top: 12px;
          font-size:13px;
        }
        th, td{
          border:1px solid #000;
          padding:6px 6px;
          vertical-align:middle;
        }
        th{
          font-weight:700;
          text-align:center;
        }
        td{
          text-align:center;
        }
        .left{ text-align:left; }
        .right{ text-align:right; }
        .totRow td{
          font-weight:700;
        }
        .footerTables{
          margin-top: 10px;
          display:flex;
          justify-content:center;
        }
        .pfBreak{
          width: 380px;
          border-collapse:collapse;
          font-size:13px;
        }
        .pfBreak th, .pfBreak td{
          border:1px solid #000;
          padding:6px;
        }
        .actions{
          max-width:1100px;
          margin: 12px auto 0;
          display:flex;
          justify-content:flex-end;
          gap:10px;
        }
        .btn{
          border:1px solid #000;
          background:#fff;
          padding:8px 14px;
          cursor:pointer;
          font-weight:700;
        }
        .muted{
          font-size:12px;
          margin-top: 10px;
          text-align:center;
        }

        /* Print */
        @media print{
          .actions{ display:none; }
          .print-root{ padding:0; }
          .page{ border:none; padding:0; max-width:unset; margin:0; }
          @page { margin: 10mm; }
        }
      `}</style>

      {/* Buttons (hidden in print) */}
      <div className="actions">
        <button className="btn" onClick={handlePrint}>Print</button>
      </div>

      <div className="page">
        {/* Header */}
        <div className="headerBox">
          <div className="logoBox">LOGO</div>

          <div className="companyText">
            <div className="name">{companyName}</div>
            <div className="sub">{companyLine1}</div>
            <div className="sub">{companyLine2}</div>
            <div className="addr">{companyAddr}</div>
            <div className="addr">{companyPhone}</div>
          </div>

          <div style={{ width: 120 }} />
        </div>

        {/* Title */}
        <div className="title">
          Register of Wages for the month of {monthName} &nbsp; {year}
        </div>

        {/* Main table */}
        <table>
          <thead>
            <tr>
              <th>S.No</th>
              <th>UAN No</th>
              <th>PF No</th>
              <th>Emp Code</th>
              <th>Name of workman</th>
              <th>Basic Wages</th>
              <th>Leave</th>
              <th>LOP</th>
              <th>Paid Basic</th>
              <th>Difference</th>
              <th>Paid Gross</th>
              <th>PF 12%</th>
              <th>8.33%</th>
              <th>3.67%</th>
              <th>Branch</th>
              <th>Signature</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={16} className="left">Loading...</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={16}>No Records Found</td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{r.fld_pfuanno || ""}</td>
                  <td>{r.fld_pfno || ""}</td>
                  <td>{r.fld_employeecode || ""}</td>
                  <td className="left">{r.fld_employee || r.fld_Employee || ""}</td>
                  <td className="right">{toNum(r.wages).toFixed(2)}</td>
                  <td className="right">{toNum(r.fld_absence).toFixed(2)}</td>
                  <td className="right">{toNum(r.fld_lop).toFixed(2)}</td>
                  <td className="right">{toNum(r.fld_paidbasic).toFixed(2)}</td>
                  <td className="right">{toNum(r.otherallowance).toFixed(2)}</td>
                  <td className="right">{toNum(r.totalwages).toFixed(2)}</td>
                  <td className="right">{toNum(r.fld_pf).toFixed(2)}</td>
                  <td className="right">{toNum(r.fld_pf8).toFixed(2)}</td>
                  <td className="right">{toNum(r.fld_pf3).toFixed(2)}</td>
                  <td className="left">{r.fld_branchname || r.fld_BranchName || ""}</td>
                  <td />
                </tr>
              ))
            )}

            {/* Totals row */}
            {rows.length > 0 && !loading && (
              <tr className="totRow">
                <td colSpan={5}>Total</td>
                <td className="right">{roundAwayFromZero(totals.tBasic).toFixed(2)}</td>
                <td className="right">{roundAwayFromZero(totals.tLeave).toFixed(2)}</td>
                <td className="right">{roundAwayFromZero(totals.tLop).toFixed(2)}</td>
                <td className="right">{roundAwayFromZero(totals.tPaidBasic).toFixed(2)}</td>
                <td className="right">{roundAwayFromZero(totals.tDiff).toFixed(2)}</td>
                <td className="right">{roundAwayFromZero(totals.tPaidGross).toFixed(2)}</td>
                <td className="right">{roundAwayFromZero(totals.tPf12).toFixed(2)}</td>
                <td className="right">{roundAwayFromZero(totals.tPf833).toFixed(2)}</td>
                <td className="right">{roundAwayFromZero(totals.tPf367).toFixed(2)}</td>
                <td />
                <td />
              </tr>
            )}
          </tbody>
        </table>

        {/* PF breakdown table */}
        {rows.length > 0 && !loading && (
          <div className="footerTables">
            <table className="pfBreak">
              <thead>
                <tr>
                  <th>Total</th>
                  <th>Percentage</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="left"><b>1. CH No 1</b></td>
                  <td><b>12.00%</b></td>
                  <td className="right"><b>{roundAwayFromZero(totals.tPf12).toFixed(2)}</b></td>
                </tr>
                <tr>
                  <td className="left"></td>
                  <td><b>3.67%</b></td>
                  <td className="right"><b>{roundAwayFromZero(totals.tPf367).toFixed(2)}</b></td>
                </tr>
                <tr>
                  <td className="left"><b>2. CH No 10</b></td>
                  <td><b>8.33%</b></td>
                  <td className="right"><b>{roundAwayFromZero(totals.tPf833).toFixed(2)}</b></td>
                </tr>
                <tr>
                  <td className="left"><b>3. CH No 2</b></td>
                  <td><b>0.50%</b></td>
                  <td className="right"><b>{roundAwayFromZero(totals.chNo2).toFixed(2)}</b></td>
                </tr>
                <tr>
                  <td className="left"><b>4. CH No 21</b></td>
                  <td><b>0.50%</b></td>
                  <td className="right"><b>{roundAwayFromZero(totals.chNo21).toFixed(2)}</b></td>
                </tr>
                <tr>
                  <td className="left"><b>5. CH No 22</b></td>
                  <td><b>0.00%</b></td>
                  <td className="right">-</td>
                </tr>
                <tr>
                  <td colSpan={2}><b>Total</b></td>
                  <td className="right"><b>{roundAwayFromZero(totals.totalPfAll).toFixed(2)}</b></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="muted">
          Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
        </div>
      </div>
    </div>
  );
};

export default EmployeePFListPrint;
